<script setup lang="ts">
import { ref, computed, onUnmounted, type ComponentPublicInstance } from 'vue'
import FileInput from './FileInput.vue'

interface ArchivosFirma {
	llave?: File
}

interface Estado {
	estaProcesando: boolean
	error: string | null
	progresoFirma: number
}

interface MensajeWorker {
	tipo: 'progreso' | 'resultado'
	actual?: number
	total?: number
	resultado?: {
		success: boolean
		firmas?: string[]
		error?: string
	}
}

const props = defineProps<{
	showModal?: boolean
	cadenasAFirmar: string[]
	certificadoResponsable: string
}>()

const emit = defineEmits<{
	'firma-completada': [data: { firmas: string[]; face: string; signatureFace: string }]
	'firma-error': [string]
}>()

const refs = {
	llave: ref<HTMLInputElement | null>(null)
}

const password = ref('')
const archivos = ref<ArchivosFirma>({})
const documentosFirmadosLocal = ref(false)

const estado = ref<Estado>({
	estaProcesando: false,
	error: null,
	progresoFirma: 0
})

let worker: Worker | null = null

onUnmounted(() => {
	if (worker) {
		worker.terminate()
		worker = null
	}
})

const isFormValid = computed(() => archivos.value.llave !== undefined && password.value.trim().length > 0)

const setFileRef = (el: Element | ComponentPublicInstance | null, tipo: keyof typeof refs) => {
	if (el instanceof HTMLInputElement || el === null) {
		refs[tipo].value = el
	}
}

const onFileSelected = (event: Event, tipo: keyof ArchivosFirma): void => {
	const input = event.target as HTMLInputElement
	if (input.files?.[0]) {
		archivos.value[tipo] = input.files[0]
	}
}

const getFile = async (file: File): Promise<string> =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader()

		reader.onload = () => {
			if (!reader.result) {
				reject(new Error('No se pudo leer el archivo'))
				return
			}

			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1]
				resolve(base64)
			} else {
				const uint8Array = new Uint8Array(reader.result)
				const textDecoder = new TextDecoder('utf-8')
				resolve(textDecoder.decode(uint8Array))
			}
		}

		reader.onerror = () => {
			reject(new Error('Error al leer el archivo: ' + (reader.error?.message || 'Error desconocido')))
		}

		reader.readAsDataURL(file)
	})

const firmarDocumentos = async () => {
	try {
		estado.value.estaProcesando = true
		estado.value.error = null
		estado.value.progresoFirma = 0

		// Validar que se hayan seleccionado los archivos
		if (!archivos.value.llave || !password.value) {
			estado.value.error = 'Por favor, complete todos los campos'
			return
		}

		const llaveBase64 = await getFile(archivos.value.llave)

		worker = new Worker(new URL('../workers/firma.worker.ts', import.meta.url), {
			type: 'module'
		})

		worker.onmessage = (e: MessageEvent<MensajeWorker>) => {
			if (e.data.tipo === 'progreso' && e.data.actual !== undefined && e.data.total !== undefined) {
				estado.value.progresoFirma = Math.round((e.data.actual / e.data.total) * 100)
			} else if (e.data.tipo === 'resultado') {
				if (e.data.resultado?.success && e.data.resultado.firmas) {
					emit('firma-completada', {
						firmas: e.data.resultado.firmas,
						face: '',
						signatureFace: ''
					})
					documentosFirmadosLocal.value = true
				} else {
					const errorMessage = e.data.resultado?.error || 'Error desconocido al firmar'
					estado.value.error = errorMessage
					emit('firma-error', errorMessage)
				}
				estado.value.estaProcesando = false
				worker?.terminate()
				worker = null
			}
		}

		worker.onerror = error => {
			const errorMessage = 'Error al procesar la firma: ' + (error.message || 'Error desconocido')
			estado.value.error = errorMessage
			estado.value.estaProcesando = false
			emit('firma-error', errorMessage)
			worker?.terminate()
			worker = null
		}

		worker.postMessage({
			certificadoText: props.certificadoResponsable,
			llaveText: llaveBase64,
			password: password.value,
			cadenasAFirmar: props.cadenasAFirmar.join('\n')
		})
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Error al procesar la firma'
		estado.value.error = errorMessage
		estado.value.estaProcesando = false
		emit('firma-error', errorMessage)
		worker?.terminate()
		worker = null
	}
}

const fileInputs = {
	llave: {
		label: 'Llave privada (.key)',
		accept: '.key',
		icon: 'fa-key'
	}
}
</script>
<template>
	<div class="firma-container">
		<div class="documentos-info mb-2">
			<div class="alert alert-info">
				<i class="fas fa-file-signature me-2"></i>
				Se firmarán {{ cadenasAFirmar.length }} documento{{ cadenasAFirmar.length !== 1 ? 's' : '' }}
			</div>
		</div>

		<div class="form-fields">
			<div v-if="documentosFirmadosLocal" class="alert alert-success mb-4">
				<i class="fas fa-check-circle me-2"></i>
				Los documentos ya han sido firmados
			</div>
			<template v-else>
				<FileInput
					v-for="(config, tipo) in fileInputs"
					:key="tipo"
					:id="tipo"
					:label="config.label"
					:accept="config.accept"
					:icon="config.icon"
					:value="archivos[tipo] || null"
					@change="(event: Event) => onFileSelected(event, tipo)"
					:ref="el => setFileRef(el, tipo)" />

				<div class="form-group">
					<label for="password" class="required-field">Contraseña:</label>
					<div class="password-input-container">
						<input type="password" id="password" v-model="password" required class="form-control" placeholder="Ingrese su contraseña" />
						<i class="fas fa-lock input-icon"></i>
					</div>
				</div>

				<div class="form-actions">
					<button @click="firmarDocumentos" :disabled="!isFormValid || estado.estaProcesando" class="btn btn-primary btn-lg w-100">
						<template v-if="estado.estaProcesando">
							<div class="d-flex align-items-center justify-content-center">
								<div class="spinner-border spinner-border-sm me-2" role="status">
									<span class="visually-hidden">Firmando...</span>
								</div>
								<span>Firmando... {{ estado.progresoFirma }}%</span>
							</div>
						</template>
						<template v-else>
							<i class="fas fa-signature me-2"></i>
							Firmar Documentos
						</template>
					</button>
				</div>
			</template>
		</div>

		<div v-if="estado.error" class="alert alert-danger mt-4">
			<i class="fas fa-exclamation-circle me-2"></i>
			{{ estado.error }}
		</div>
	</div>
</template>
<style scoped>
.firma-container {
	padding: 1.5rem;
	width: 100%;
	max-width: 700px;
	margin: 0 auto;
	background-color: white;
	border-radius: 12px;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
	box-sizing: border-box;
}

.form-fields {
	width: 100%;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
}

.required-field::after {
	content: '*';
	color: #dc3545;
	margin-left: 4px;
}

.password-input-container {
	position: relative;
}

.input-icon {
	position: absolute;
	right: 1rem;
	top: 50%;
	transform: translateY(-50%);
	color: #6c757d;
}

.form-control {
	padding-right: 2.5rem;
}

.form-group {
	margin-bottom: 10px;
}

.form-group label {
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 500;
	color: #2c3e50;
}

.alert {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 1rem;
	border-radius: 8px;
	margin-bottom: 0;
}

.alert-info {
	background-color: #e7f2ff;
	color: #004085;
	border: none;
}

.alert-danger {
	background-color: #fff3f3;
	color: #721c24;
	border: none;
}

.mb-4 {
	margin-bottom: 1.5rem;
}

.me-2 {
	margin-right: 0.5rem;
}

.mt-4 {
	margin-top: 1.5rem;
}

.w-100 {
	width: 100%;
}

.form-actions {
	margin-top: auto;
	padding-top: 10px;
}

.spinner-border {
	width: 1rem;
	height: 1rem;
	border-width: 0.15em;
}

.visually-hidden {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

@media (max-width: 768px) {
	.firma-container {
		padding: 1rem;
		border-radius: 0;
		box-shadow: none;
	}
}

@media (max-width: 480px) {
	.firma-container {
		padding: 0.75rem;
	}
}
</style>
